import React, { useState, useEffect } from "react";
import {
  IndianRupee,
  TrendingUp,
  Users,
  BarChart3,
  PieChart,
  Download,
  RefreshCw,
  Target,
  Award,
  FileText,
  ChevronDown,
} from "lucide-react";
import hrAnalyticsService from "../../../api/services/hrAnalyticsService";
import { formatIndianNumber } from "../../../utils/amountFormatter";
import Button from "../../../components/common/Button/Button";
import DateRangeFilter from "../../../components/common/DateRangeFilter";

// CSV Export Function
const exportToCSV = (salaryMetrics, departmentSalaries, salaryBands, topEarners, formatLakhs, formatLakhsWithTwoDecimals, formatSalaryDisplay, formatCurrencyDisplay) => {
  const rows = [];

  // Add header row
  rows.push([
    'Salary Trends & Analysis Report',
    `Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
  ]);
  rows.push([]); // Empty row

  // Key Metrics Section
  rows.push(['Key Metrics']);
  rows.push(['Metric', 'Value']);
  rows.push(['Average Salary', formatLakhsWithTwoDecimals(salaryMetrics.avgSalary.current)]);
  rows.push(['Median Salary', formatSalaryDisplay(salaryMetrics.medianSalary.current)]);
  rows.push(['Total Payroll', formatCurrencyDisplay(salaryMetrics.totalPayroll.current)]);
  rows.push([]); // Empty row

  // Department Salary Analysis Section
  if (departmentSalaries && departmentSalaries.length > 0) {
    rows.push(['Department Salary Analysis']);
    rows.push([
      'Department',
      'Employees',
      'Avg Salary',
      'Median',
      'Pay Range'
    ]);

    departmentSalaries.forEach((dept) => {
      rows.push([
        dept.department || '',
        dept.employees || 0,
        formatSalaryDisplay(dept.avgSalary),
        formatSalaryDisplay(dept.medianSalary),
        `${formatLakhs(dept.payRange?.min || 0)} - ${formatLakhs(dept.payRange?.max || 0)}`
      ]);
    });
    rows.push([]); // Empty row
  }

  // Salary Distribution Section
  if (salaryBands && salaryBands.length > 0) {
    rows.push(['Salary Distribution']);
    rows.push(['Band', 'Employees', 'Percentage']);

    salaryBands.forEach((band) => {
      rows.push([
        band.band || '',
        band.employees || 0,
        `${band.percentage || 0}%`
      ]);
    });
    rows.push([]); // Empty row
  }

  // Top Earners Section
  if (topEarners && topEarners.length > 0) {
    rows.push(['Top Earners']);
    rows.push([
      'Rank',
      'Name',
      'Role',
      'Department',
      'Salary'
    ]);

    topEarners.forEach((earner, index) => {
      rows.push([
        `#${index + 1}`,
        earner.name || '',
        earner.role || '',
        earner.department || '',
        formatSalaryDisplay(earner.salary)
      ]);
    });
  }

  // Convert rows to CSV format
  const csvContent = rows.map(row => {
    return row.map(cell => {
      // Escape quotes and wrap in quotes if contains comma, newline, or quote
      const cellValue = String(cell || '');
      if (cellValue.includes(',') || cellValue.includes('\n') || cellValue.includes('"')) {
        return `"${cellValue.replace(/"/g, '""')}"`;
      }
      return cellValue;
    }).join(',');
  }).join('\n');

  // Create and download CSV file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `Salary_Trends_Report_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const SalaryTrends = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("year");
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [viewType, setViewType] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [salaryData, setSalaryData] = useState(null);

  const KpiCard = ({ icon: Icon, title, value, subtitle, color, trend }) => {
      let bgColor = "bg-[#0F2942]";
      if (color?.includes("brand") || color?.includes("blue")) bgColor = "bg-[#0F2942]";
      else if (color?.includes("red")) bgColor = "bg-[#DC2626]";
      else if (color?.includes("green") || color?.includes("emerald")) bgColor = "bg-[#16A34A]";
      else if (color?.includes("purple") || color?.includes("indigo")) bgColor = "bg-[#7C3AED]";
      else if (color?.includes("yellow") || color?.includes("orange") || color?.includes("amber")) bgColor = "bg-[#D97706]";

      return (
          <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 flex items-center justify-between shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 group">
              <div className="space-y-4 flex-1">
                  <h3 className="text-[11px] font-bold tracking-[0.1em] text-slate-500 uppercase">
                      {title}
                  </h3>
                  <div className="flex items-baseline space-x-2">
                      <span className="text-[28px] font-bold text-slate-900 leading-none tracking-tight">
                          {value}
                      </span>
                      {(subtitle || trend) && (
                          <div className="flex items-center space-x-2">
                              {subtitle && (
                                  <span className="text-sm font-medium text-slate-500">
                                      {subtitle}
                                  </span>
                              )}
                              {trend && (
                                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${trend.color} shadow-sm`}>
                                      {trend.label}
                                  </span>
                              )}
                          </div>
                      )}
                  </div>
              </div>
              <div className={`w-12 h-12 rounded-full ${bgColor} flex items-center justify-center text-white shadow-sm transform transition-transform duration-300 group-hover:scale-110 flex-shrink-0 ml-4`}>
                  <Icon className="w-5 h-5" />
              </div>
          </div>
      );
  };

  // Real data states
  const [salaryMetrics, setSalaryMetrics] = useState({
    avgSalary: { current: 0, previous: 0, growth: 0 },
    medianSalary: { current: 0, previous: 0, growth: 0 },
    totalPayroll: { current: 0, previous: 0, growth: 0 },
    payEquityIndex: { current: 0, previous: 0, target: 0.95 },
  });

  // Load salary trends data on component mount
  useEffect(() => {
    loadSalaryTrendsData();
  }, [selectedPeriod, selectedDepartment, dateRange]);

  const loadSalaryTrendsData = async () => {
    try {
      setLoading(true);
      const response = await hrAnalyticsService.getComprehensiveSalaryTrends(
        selectedPeriod,
        selectedDepartment,
        dateRange.startDate,
        dateRange.endDate
      );
      // The API returns { responseCode: 200, data: { keyMetrics: {...}, departmentBreakdown: [...] } }
      const apiData = response?.data || response;

      if (apiData) {
        setSalaryData(apiData);

        // Use real growth data from backend API
        const currentMetrics = apiData.keyMetrics || {};
        const previousMetrics = apiData.previousYearMetrics || {};
        const growthMetrics = apiData.growthMetrics || {};

        // Update metrics with real data from backend
        setSalaryMetrics({
          avgSalary: {
            current: currentMetrics.avgSalary || 0,
            previous: previousMetrics.avgSalary || 0,
            growth: growthMetrics.avgSalaryGrowth || 0,
          },
          medianSalary: {
            current: currentMetrics.medianSalary || 0,
            previous: previousMetrics.medianSalary || 0,
            growth: growthMetrics.medianSalaryGrowth || 0,
          },
          totalPayroll: {
            current: currentMetrics.totalPayroll || 0,
            previous: previousMetrics.totalPayroll || 0,
            growth: growthMetrics.totalPayrollGrowth || 0,
          },
          payEquityIndex: {
            current: currentMetrics.payEquityIndex || 0,
            previous: 0.91, // Default previous value
            target: 0.95,
          },
        });
      }
    } catch (error) {
      // Handle error
    } finally {
      setLoading(false);
    }
  };

  // Use real data from API
  const departmentSalaries = salaryData?.departmentBreakdown || [];

  const salaryBands = salaryData?.salaryDistribution || [];

  const payEquityAnalysis = salaryData?.payEquityAnalysis || [];

  // Use real data from API
  const topEarners = salaryData?.topEarners || [];

  const salaryIncreases = salaryData?.salaryIncreases || [];

  const formatCurrency = (amount) => {
    if (typeof amount === "string") return amount;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatLakhs = (amount) => {
    return `₹${(amount / 100000).toFixed(1)}L`;
  };

  const formatLakhsWithTwoDecimals = (amount) => {
    return `₹${(amount / 100000).toFixed(2)}L`;
  };

  // Helper function to format salary
  const formatSalaryDisplay = (amount, isPercentage = false) => {
    if (isPercentage) return amount; // Don't mask percentages
    return formatLakhs(amount);
  };

  const formatCurrencyDisplay = (amount) => {
    return formatCurrency(amount);
  };

  const getEquityColor = (index) => {
    if (index >= 0.95) return "text-green-600 bg-green-50";
    if (index >= 0.9) return "text-yellow-600 bg-yellow-50";
    return "text-red-600 bg-red-50";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "on_track":
        return "text-green-600 bg-green-50";
      case "needs_attention":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  if (loading && !salaryData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="h-5 w-5 animate-spin" />
          <span>Loading salary trends...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            Salary Trends & Analysis
          </h2>
          <p className="text-sm text-gray-600">
            Comprehensive compensation analysis and pay equity insights
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            <select
              value={selectedPeriod}
              onChange={(e) => {
                const newPeriod = e.target.value;
                setSelectedPeriod(newPeriod);
                if (newPeriod !== "custom") {
                  setDateRange({ startDate: "", endDate: "" });
                }
              }}
              className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 focus:border-blue-500 cursor-pointer"
            >
              <option value="custom">Custom Range</option>
              <option value="current-month">Current Month</option>
              <option value="last-month">Last Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="all-data">All Data</option>
            </select>
          </div>

          {selectedPeriod === "custom" && (
            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={(range) => setDateRange(range)}
              />
            </div>
          )}

          {/* CSV Export */}
          <Button
            variant="outline"
            onClick={() => exportToCSV(
              salaryMetrics,
              departmentSalaries,
              salaryBands,
              topEarners,
              formatLakhs,
              formatLakhsWithTwoDecimals,
              formatSalaryDisplay,
              formatCurrencyDisplay
            )}
            className="flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          icon={IndianRupee}
          title="Average Salary"
          value={formatLakhsWithTwoDecimals(salaryMetrics.avgSalary.current)}
          color="bg-[#0F2942]"
        />
        <KpiCard
          icon={Target}
          title="Median Salary"
          value={formatSalaryDisplay(salaryMetrics.medianSalary.current)}
          color="bg-emerald-600"
        />
        <KpiCard
          icon={Users}
          title="Total Payroll"
          value={formatSalaryDisplay(salaryMetrics.totalPayroll.current)}
          color="bg-purple-600"
        />
      </div>

      {/* Department Salary Analysis */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Department Salary Analysis
          </h3>
          <button className="text-gray-400 hover:text-gray-600">
            <BarChart3 className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-900">
                  Department
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">
                  Employees
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">
                  Avg Salary
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">
                  Median
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">
                  Pay Range
                </th>
              </tr>
            </thead>
            <tbody>
              {departmentSalaries.length > 0 ? (
                departmentSalaries.map((dept, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-3 px-4 max-w-[200px]">
                      <p className="text-sm font-medium text-gray-900 break-words" title={dept.department}>
                        {dept.department}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-600">{formatIndianNumber(dept.employees)}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm font-semibold text-gray-900">
                        {formatSalaryDisplay(dept.avgSalary)}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-600">
                        {formatSalaryDisplay(dept.medianSalary)}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-600">
                        {formatLakhs(dept.payRange.min)} -{" "}
                        {formatLakhs(dept.payRange.max)}
                      </p>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="py-8 px-4 text-center text-gray-500"
                  >
                    No department data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Salary Distribution & Top Earners */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Salary Distribution */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Salary Distribution
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <PieChart className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            {salaryBands.length > 0 ? (
              salaryBands.map((band, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">
                        {band.band}
                      </span>
                      <span className="text-sm text-gray-600">
                        {formatIndianNumber(band.employees)} ({band.percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-[#0F2942] h-2 rounded-full"
                        style={{ width: `${band.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No salary distribution data found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  No salary distribution data is available for the selected period.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Top Earners */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Top Earners</h3>
            <button className="text-gray-400 hover:text-gray-600">
              <Award className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            {topEarners.length > 0 ? (
              topEarners.map((earner, index) => (
                <div
                  key={earner.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {earner.name}
                    </p>
                    <p className="text-xs text-gray-600 break-words" title={`${earner.role} • ${earner.department}`}>
                      {earner.role} • {earner.department}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">
                      {formatSalaryDisplay(earner.salary)}
                    </p>
                    <div className="flex items-center">
                      <span className="text-xs text-gray-500">#{index + 1}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No top earners data found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  No top earners data is available for the selected period.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryTrends;
